export type CropArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Export a crop from a loaded <img>. Crop coordinates are in displayed CSS pixels
 * (react-image-crop PixelCrop); they are scaled to the image's natural size.
 */
export async function cropImageToFile(
  image: HTMLImageElement,
  crop: CropArea,
  options: { mimeType?: string; fileName?: string; quality?: number } = {}
): Promise<File> {
  if (!crop.width || !crop.height) {
    throw new Error("Select a crop area before continuing.");
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const sourceX = crop.x * scaleX;
  const sourceY = crop.y * scaleY;
  const sourceWidth = Math.max(1, crop.width * scaleX);
  const sourceHeight = Math.max(1, crop.height * scaleY);

  const canvas = document.createElement("canvas");
  const width = Math.max(1, Math.round(sourceWidth));
  const height = Math.max(1, Math.round(sourceHeight));
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not prepare the cropped image.");
  }

  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    width,
    height
  );

  const mimeType =
    options.mimeType === "image/png" || options.mimeType === "image/jpeg"
      ? options.mimeType
      : "image/jpeg";
  const quality = mimeType === "image/jpeg" ? options.quality ?? 0.92 : undefined;
  const extension = mimeType === "image/png" ? "png" : "jpg";
  const baseName = (options.fileName || "signature").replace(/\.[^.]+$/, "") || "signature";

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) resolve(result);
        else reject(new Error("Could not export the cropped image."));
      },
      mimeType,
      quality
    );
  });

  return new File([blob], `${baseName}.${extension}`, { type: mimeType });
}
